import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, errMsg } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { StatusBadge } from '@/components/common';
import { groupDocuments, accessState, matchesDocument } from '@/lib/documentCategories.mjs';

function ThemeCheck({ mixed, ...props }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = mixed; }, [mixed]);
  return <input type="checkbox" ref={ref} aria-checked={mixed ? 'mixed' : props.checked} {...props} />;
}

export default function AgentDocumentThemes({ documents = [], agentId, onReload, disabled }) {
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const change = async (selector, enabled) => {
    setBusy(true);
    try {
      const r = await api.post('/documents/bulk-access', { ...selector, agent_id: agentId, enabled });
      toast.success(`${r.data.matched} document(s) ${enabled ? 'affecté(s)' : 'retiré(s)'}`);
      await onReload();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const groups = groupDocuments(documents).filter((g) => g.documents.some((d) => matchesDocument(d, query)));
  return <div className="space-y-3">
    <Input placeholder="Rechercher un thème, un document ou une URL…" value={query} onChange={(e) => setQuery(e.target.value)} />
    <p className="text-xs text-muted-foreground">Cochez un thème pour affecter tous ses documents actuels, ou ouvrez-le pour choisir individuellement. L’accès est modifié immédiatement, sans publication. Une case partiellement cochée indique une sélection partielle.</p>
    {documents.length >= 2000 && <p className="text-xs text-amber-600">La liste affiche jusqu’à 2 000 documents. Une action sur un thème concerne tous les documents de cette catégorie dans la base.</p>}
    {groups.map((g) => {
      const state = accessState(g.documents, agentId);
      return <details key={g.category} className="border rounded-md" open={query.trim() ? true : undefined}>
        <summary className="p-3 cursor-pointer">
          <span className="inline-flex gap-3 items-center ml-2">
            <ThemeCheck checked={state.checked} mixed={state.mixed} disabled={disabled || busy}
              aria-label={`Affecter tout le thème ${g.label}`} onClick={(e) => e.stopPropagation()}
              onChange={(e) => change({ category: g.category }, e.target.checked)} />
            <b>{g.label}</b><span className="text-xs text-muted-foreground">{state.count}/{g.documents.length} accessibles · thème complet</span>
          </span>
        </summary>
        <div className="px-4 pb-4 space-y-2 max-h-80 overflow-y-auto">{g.documents.filter((d) => matchesDocument(d, query)).map((d) => <label key={d.id} className="flex gap-2 items-start text-sm">
          <input type="checkbox" checked={(d.agent_ids || []).includes(agentId)} disabled={disabled || busy}
            data-testid={`agent-doc-${d.id}`} onChange={(e) => change({ document_ids: [d.id] }, e.target.checked)} />
          <span className="break-words">{d.title} <StatusBadge status={d.status} />{d.excluded && <span className="text-amber-600"> · Exclu du RAG</span>}</span>
        </label>)}</div>
      </details>;
    })}
    {!groups.length && <p className="text-sm text-muted-foreground">Aucun document correspondant.</p>}
    <p className="text-xs text-muted-foreground">Pour les futures pages web, définissez les agents autorisés et la catégorie sur leur source. Classez les documents depuis la base documentaire.</p>
  </div>;
}
